import type { FC } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { CertRef, PropertyRef } from 'src/components/SupportCase/types';

interface CaseContextLineProps {
  certification: CertRef | null;
  property: PropertyRef | null;
  /** Rendered as a quiet caption rather than body text (panel header). */
  compact?: boolean;
  /**
   * One line that ellipsises instead of wrapping (ENG-4299).
   *
   * The panel's meta row has one flexible slot and this is it: the header stays two
   * lines tall whatever the household name is, and the full value travels in the
   * tooltip the caller attaches. Wrapping is what turned the context line into a
   * 67 px column four lines tall.
   */
  singleLine?: boolean;
  /** Present only when the case can be re-linked (the case page and the panel). */
  onLink?: () => void;
}

/** The unlinked sentence, long form and the quiet form the panel has room for. */
export const UNLINKED_LABEL = 'Not linked to a property or certification';
export const UNLINKED_LABEL_COMPACT = 'Not linked to a property';

/** The property label: the link's own name, or the tail of the cert's context line. */
const propertyLabel = (
  certification: CertRef | null,
  property: PropertyRef | null
): string =>
  property?.name ?? certification?.context_line?.split(' · ')[1] ?? '';

/**
 * The same content as one plain string (ENG-4299).
 *
 * The panel truncates this line, so it needs the whole value for the tooltip. Built
 * here rather than in the header so the rendered line and the tooltip cannot drift.
 */
export const contextLineText = ({
  certification,
  property,
}: {
  certification: CertRef | null;
  property: PropertyRef | null;
}): string => {
  if (!certification && !property) {
    return UNLINKED_LABEL_COMPACT;
  }
  const name = propertyLabel(certification, property);
  if (!certification) {
    return name;
  }
  return [
    certification.household_name,
    name,
    certification.log_number ? `Log ${certification.log_number}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * What this conversation is about, as names and links (ENG-4251, PA-13).
 *
 * One component for the panel header, the case page and the list rows, so the three
 * surfaces cannot drift. Three states, and never a dangling separator:
 *
 *   certification  Paul Williams · 102 Charlton · Log 6212
 *   property       102 Charlton
 *   neither        Not linked to a property   [Link a property]
 *
 * Names, never ids: the household and the property are what a compliance user
 * recognises; the log number is the only numeric identifier they use.
 */
const CaseContextLine: FC<CaseContextLineProps> = ({
  certification,
  property,
  compact = false,
  singleLine = false,
  onLink,
}) => {
  const variant = compact ? 'caption' : 'body2';
  const linkLabel = certification
    ? certification.log_number
      ? `Log ${certification.log_number}`
      : 'Open certification'
    : '';
  const name = propertyLabel(certification, property);

  const propertyNode = property ? (
    <Link
      component={RouterLink}
      to={property.url}
      variant={variant}
      underline="hover"
    >
      {property.name}
    </Link>
  ) : (
    <Typography variant={variant} color="text.secondary">
      {name}
    </Typography>
  );

  const certLink = certification ? (
    <Link
      component={RouterLink}
      to={certification.url}
      variant={variant}
      underline="hover"
      aria-label={`Open certification for ${
        certification.household_name || name
      }`}
    >
      {linkLabel}
    </Link>
  ) : null;

  if (!certification && !property) {
    // The short form is the panel's, where the line has one row to say it in. The
    // case page and the list keep the full sentence.
    const label = singleLine ? UNLINKED_LABEL_COMPACT : UNLINKED_LABEL;
    const link = onLink ? (
      <Link
        component="button"
        type="button"
        variant={variant}
        onClick={onLink}
        underline="hover"
      >
        Link a property
      </Link>
    ) : null;
    if (singleLine) {
      return (
        <Box
          data-testid="case-context-line"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Typography component="span" variant={variant} color="text.secondary">
            {label}
          </Typography>
          {link ? <> {link}</> : null}
        </Box>
      );
    }
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        data-testid="case-context-line"
      >
        <Typography variant={variant} color="text.secondary">
          {label}
        </Typography>
        {link}
      </Stack>
    );
  }

  if (singleLine) {
    /* One inline run: the box owns the ellipsis, so the text must be inline flow. */
    const separator = certification?.household_name && name ? ' · ' : '';
    return (
      <Box
        data-testid="case-context-line"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {certification?.household_name ? (
          <Typography component="span" variant={variant} color="text.secondary">
            {certification.household_name}
            {separator}
          </Typography>
        ) : null}
        {certification ? (
          <>
            {property ? (
              <Link
                component={RouterLink}
                to={property.url}
                variant={variant}
                underline="hover"
              >
                {property.name}
              </Link>
            ) : (
              <Typography
                component="span"
                variant={variant}
                color="text.secondary"
              >
                {name}
              </Typography>
            )}
            {name ? ' · ' : null}
            {certLink}
          </>
        ) : (
          propertyNode
        )}
      </Box>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      data-testid="case-context-line"
    >
      {certification ? (
        <>
          {certification.household_name ? (
            <Typography variant={variant} color="text.secondary">
              {certification.household_name}
            </Typography>
          ) : null}
          {certification.household_name && name ? (
            <Typography variant={variant} color="text.secondary" aria-hidden>
              ·
            </Typography>
          ) : null}
          {propertyNode}
          {certLink}
        </>
      ) : (
        propertyNode
      )}
    </Stack>
  );
};

export default CaseContextLine;
